import React, { useEffect, useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import { useListAIUserThreads } from '../../../../api/denaliAssistant'
import ThreadItem from '../ThreadItem'
import { useLayoutContext } from '../contexts/LayoutProvider'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import NavbarTitle from './NavbarTitle'
import NavItem from './NavItem'

const useStyles = makeStyles((theme) => ({
  container: {
    padding: 'var(--padding-sm) 0 0 0',
    overflowX: 'auto',
    border: 'var(--border-neutral-thin)',
    borderColor: 'var(--color-texture-300)',
    height: 'calc(100% - 0px)',
    display: 'flex',
    transition: 'width .2s ease-in-out',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-sm)',
    background: 'linear-gradient(color-mix(in srgb, var(--color-texture-300) 90%, transparent), color-mix(in srgb, var(--color-texture-300) 100%, transparent))'
  },
  header: {
    padding: 'var(--padding-sm)',
    borderBottom: '1px solid var(--border-color-neutral)',
    flexShrink: 0
  },
  newChatButton: {
    width: '100%',
    marginBottom: 'var(--margin-sm)'
  },
  searchBox: {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--color-neutral-050)',
      paddingTop: 'var(--dim-1)',
      paddingBottom: 'var(--dim-1)',
      fontSize: 'var(--font-size-xs)',
      borderRadius: 'var(--border-radius-sm)',
      border: 'var(--border-neutral-thin)',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.15)'
      },
      '&.Mui-focused': {
        borderColor: 'var(--color-ai-400)'
      }
    },
    '& .MuiInputBase-input': {
      padding: 'var(--padding-xs) var(--padding-sm)',
      fontWeight: 'normal',
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-contrast-neutral-050)',
      '&::placeholder': {
        color: 'var(--color-neutral-500)',
        opacity: 1
      }
    },
    '& .MuiInputAdornment-root': {
      marginLeft: 'var(--margin-xs)'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    }
  },
  threadList: {
    flex: 1,
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'var(--color-neutral-600)',
      borderRadius: '3px'
    }
  },
  emptyState: {
    textAlign: 'center',
    padding: 'var(--padding-lg)',
    color: 'var(--color-neutral-400)'
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px'
  }
}))

function MobileNavbarBody ({ activeThreadId }) {
  const classes = useStyles()
  const { url } = useRouteMatch()
  const { toggleNav, navCollapsed } = useLayoutContext()
  const { data, isLoading, error, refetch } = useListAIUserThreads()
  const [searchQuery] = useState('')

  // Refetch threads when a new one might have been created
  useEffect(() => {
    if (activeThreadId && activeThreadId !== 'new') {
      // Small delay to ensure the thread is created on backend
      const timer = setTimeout(() => {
        refetch()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [activeThreadId, refetch])

  const threads = useMemo(() => {
    return data?.threads || []
  }, [data?.threads])

  // Filter threads based on search query
  const filteredThreads = useMemo(() => {
    if (!searchQuery.trim()) {
      return threads.slice(0, 5)
    }

    const query = searchQuery.toLowerCase()
    return threads.filter(thread => {
      // Extract thread title
      const firstMessage = thread.messages?.find(m => m.actor === 'user')
      const title = thread.name || firstMessage?.message || 'Untitled Chat'

      // Search in title and first message content
      return title.toLowerCase().includes(query) ||
             firstMessage?.message?.toLowerCase().includes(query)
    }).slice(0, 5)
  }, [threads, searchQuery])

  const handleRename = (thread) => {
    // TODO: Implement rename functionality
    console.log('Rename thread:', thread)
  }

  const handleDelete = (thread) => {
    // TODO: Implement delete functionality
    console.log('Delete thread:', thread)
  }

  return (
    <div className={classes.container} style={{ width: '100%' }}>
      <FlexColumn className='gap-0 mb-3' style={{ gap: 0 }}>
        <NavbarTitle open onIconClick={toggleNav} />
        <NavItem to='/denali-ai/new' open icon='addCircle' className='ai'>
          New Chat
        </NavItem>
        <NavItem to='/denali-ai/history' open icon='questionAnswer'>
          Chats
        </NavItem>
        <NavItem to='/denali-ai/schedules' open icon='update'>
          Schedules
        </NavItem>
        <NavItem to='/denali-ai/settings' open icon='build'>
          My Denali AI
        </NavItem>
      </FlexColumn>
      {navCollapsed ? null : (
        <div className={classes.threadList}>
          {!isLoading && !error && filteredThreads.length > 0 ? (
            <FadeIn>
              <div className='font-size-xs px-3 mb-1'>Recents</div>
              <FlexColumn style={{ gap: 0 }}>
                {filteredThreads.map((thread) => (
                  <div className='px-1' key={thread.threadId}>
                    <ThreadItem
                      thread={thread}
                      isActive={thread.threadId === activeThreadId}
                      url={url}
                      onRename={handleRename}
                      onDelete={handleDelete}
                    />
                  </div>
                ))}
              </FlexColumn>
            </FadeIn>
          ) : null}
        </div>
      )}
    </div>
  )
}

MobileNavbarBody.propTypes = {
  activeThreadId: PropTypes.string
}

MobileNavbarBody.defaultProps = {
  activeThreadId: null
}

export default MobileNavbarBody
